/**
 * Ripia Auth Module Constants
 *
 * API設定とトークンストレージキーの定義
 * @module auth/constants
 */

import env from '../../config/env';

// API設定 - セキュアな環境変数管理
export const API_CONFIG = {
  // セキュリティ強化: env.tsから安全なAPI URL取得
  BASE_URL: env.getApiConfig().baseUrl,
  ENDPOINTS: {
    // V1 エンドポイント（NestJS versioning: /api/v1/...）
    LOGIN: '/api/v1/auth/login',
    REFRESH: '/api/v1/auth/refresh',
    LOGOUT: '/api/v1/auth/logout',
    ME: '/api/v1/auth/me',
    GOOGLE: '/api/v1/auth/google',

    // メール認証エンドポイント（Phase 3追加）
    REGISTER_WITH_EMAIL_VERIFICATION: '/api/v1/auth/register',
    VERIFY_EMAIL: '/api/v1/auth/verify-email',
    RESEND_VERIFICATION_EMAIL: '/api/v1/auth/resend-verification',

    // V2 マルチテナント認証エンドポイント (Issue #282統合)
    V2_AUTH: {
      LOGIN: '/v2/auth/login',
      REFRESH: '/v2/auth/refresh',
      LOGOUT: '/v2/auth/logout',
      ME: '/v2/auth/me',
      SWITCH_TENANT: '/v2/auth/switch-tenant',
      VERIFY_TOKEN: '/v2/auth/verify-token',
      REVOKE_TOKEN: '/v2/auth/revoke-token',
      MFA_SETUP: '/v2/auth/mfa/setup',
      MFA_VERIFY: '/v2/auth/mfa/verify',
      PASSWORD_POLICY: '/v2/auth/password-policy',
      SECURITY_STATUS: '/v2/auth/security-status',
    },

    // V2 ユーザー管理エンドポイント (Enhanced Controller統合)
    V2_USERS: {
      LIST: '/v2/users',
      CREATE: '/v2/users',
      GET_BY_ID: '/v2/users/{id}',
      UPDATE: '/v2/users/{id}',
      DELETE: '/v2/users/{id}',
      GET_PERMISSIONS: '/v2/users/{id}/permissions',
      BULK_ACTION: '/v2/users/bulk-action',
      DEBUG_CONTEXT: '/v2/users/debug/tenant-context',
    },

    // V2 テナント管理エンドポイント
    V2_TENANTS: {
      LIST: '/v2/tenants',
      GET_CURRENT: '/v2/tenants/current',
      GET_BY_ID: '/v2/tenants/{id}',
      UPDATE: '/v2/tenants/{id}',
      GET_SETTINGS: '/v2/tenants/{id}/settings',
      UPDATE_SETTINGS: '/v2/tenants/{id}/settings',
      GET_ANALYTICS: '/v2/tenants/{id}/analytics',
      GET_RATE_LIMITS: '/v2/tenants/{id}/rate-limits',
    },
  },
  TIMEOUT: 10000, // 10秒タイムアウト
  RETRY_COUNT: 3,
} as const;

// Token保存キー
export const TOKEN_KEYS = {
  ACCESS_TOKEN: 'ripia_access_token',
  REFRESH_TOKEN: 'ripia_refresh_token',
  USER_DATA: 'ripia_user_data',
  // マルチテナント拡張
  TENANT_CONTEXT: 'ripia:auth:tenant_context',
  AVAILABLE_TENANTS: 'ripia_available_tenants',
  SELECTED_TENANT: 'ripia_selected_tenant',
  // エンタープライズセキュリティ拡張
  JWT_JTI: 'ripia_jwt_jti',
  DEVICE_FINGERPRINT: 'ripia_device_fingerprint',
  SECURITY_LEVEL: 'ripia_security_level',
  SESSION_ID: 'ripia_session_id',
  DEVICE_TRUST_SCORE: 'ripia_device_trust_score',
  LAST_ACTIVITY: 'ripia_last_activity',
  REMEMBER_ME: 'ripia_remember_me',
} as const;

export const LEGACY_TENANT_CONTEXT_KEYS = [
  'ripia_tenant_context',
  'salonhub_current_tenant_context',
  'salonhub_tenant_context',
] as const;

// LocalStorageキー移行（Issue #795 Phase 3 + ADR-0026 rebrand）
export const LEGACY_KEY_MIGRATIONS: Array<{ oldKey: string; newKey: string }> =
  [
    // Rebrand (ADR-0026): salonhub_* → ripia_* キー移行
    { oldKey: 'salonhub_access_token', newKey: TOKEN_KEYS.ACCESS_TOKEN },
    { oldKey: 'salonhub_refresh_token', newKey: TOKEN_KEYS.REFRESH_TOKEN },
    { oldKey: 'salonhub_user_data', newKey: TOKEN_KEYS.USER_DATA },
    {
      oldKey: 'salonhub_available_tenants',
      newKey: TOKEN_KEYS.AVAILABLE_TENANTS,
    },
    { oldKey: 'salonhub_selected_tenant', newKey: TOKEN_KEYS.SELECTED_TENANT },
  ];

// Ripia関連ストレージプレフィックス（旧salonhub_*も含む — 既存ユーザーのブラウザに残存）
// Issue #2318: Clerkプレフィックス除外 — Clerkのセッション管理はClerk.signOut()で行うべき。
// apiClientの401ハンドラがlocalStorageを部分削除するとCookieとの不整合で無限リダイレクトが発生する。
export const RIPIA_STORAGE_PREFIXES = [
  'ripia_',
  'ripia-',
  'salonhub_',
  'salonhub-',
  'auth_',
] as const;
